/**
 * Main App Component
 *
 * Phase 5: Batch Operations & Workflow Enhancements
 * - Multi-select batch operations for billable items
 * - Bulk invoice generation
 * - Improved filtering and sorting
 * - Enhanced user workflows
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth/AuthProvider.tsx';
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute.tsx';
import { LoginPage } from '@/pages/LoginPage.tsx';
import { DashboardPage } from '@/pages/DashboardPage.tsx';
import ProjectsPage from '@/pages/ProjectsPage.tsx';
import ProjectDetailPage from '@/pages/ProjectDetailPage.tsx';
import BillableItemCreatePage from '@/pages/BillableItemCreatePage.tsx';
import BillableItemDetailPage from '@/pages/BillableItemDetailPage.tsx';
import { InvoiceListPage } from '@/pages/InvoiceListPage.tsx';
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage.tsx';
import { InvoiceCreatePage } from '@/pages/InvoiceCreatePage.tsx';
import { BillingSessionPage } from '@/pages/BillingSessionPage.tsx';
import { ProjectNewPage } from '@/pages/ProjectNewPage';
import OrganizationsPage from '@/pages/OrganizationsPage.tsx';
import { OrganizationCreatePage } from '@/pages/OrganizationCreatePage.tsx';
import { OrganizationEditPage } from '@/pages/OrganizationEditPage.tsx';
import BillableItemsPage from '@/pages/BillableItemsPage.tsx';
import { ReportsPage } from '@/pages/ReportsPage.tsx';
import { SettingsPage } from '@/pages/SettingsPage.tsx';
import { NotificationsPage } from '@/pages/NotificationsPage.tsx';
import { YourIssuesPage } from '@/pages/YourIssuesPage.tsx';
import { PermitsPage } from '@/pages/PermitsPage.tsx';
import { DeliverablesDashboardPage } from '@/pages/DeliverablesDashboardPage.tsx';
import { FilesPage } from '@/pages/FilesPage.tsx';
import { UserViewPage } from '@/pages/UserViewPage.tsx';
import { ExternalViewPage } from '@/pages/ExternalViewPage.tsx';
import { PortalActionItemPage } from '@/pages/portal/PortalActionItemPage';
import { SiteVisitsPage } from '@/pages/SiteVisitsPage.tsx';
import { SiteVisitDetailPage } from '@/pages/SiteVisitDetailPage.tsx';
import { MyViewsPage } from '@/pages/MyViewsPage.tsx';
import { ThemeProvider } from '@/components/ThemeProvider';
import { FeatureProvider } from '@/lib/features/FeatureProvider';
import { FeatureGate } from '@/components/features/FeatureGate';
import { PortalManagementPage } from '@/pages/PortalManagementPage';
import { PortalAcceptInvitationPage } from '@/pages/PortalAcceptInvitationPage';
import { PortalLoginPage } from '@/pages/PortalLoginPage';
import { ExternalLoginPage } from '@/pages/ExternalLoginPage';
import { ThemeSettingsPage } from '@/pages/ThemeSettingsPage';
import { ClickUpConfigPage } from '@/pages/ClickUpConfigPage';
import { DropboxCallbackPage } from '@/pages/DropboxCallbackPage';
import { DocumentReviewListPage } from '@/pages/DocumentReviewListPage';
import { SigningPage } from '@/pages/SigningPage';
import { RoadmapPage } from '@/pages/RoadmapPage';
import { VersionUpdateBanner } from '@/components/version/VersionUpdateBanner';
import { PortalPanelProvider } from '@/lib/portal/PortalPanelContext';

function App() {
  return (
    <AuthProvider>
      <FeatureProvider>
      <ThemeProvider>
      {/* Theme 4: version-skew detection. Prompts a hard-refresh when a newer
          build is deployed while this tab is open (stale-tab bug class). Mounted
          at the shell so it survives route changes; drives the global Toaster. */}
      <VersionUpdateBanner />
      {/* R3 (tabbed panel shell): mounted ABOVE <Router> so open/active-tab
          state survives navigation — ReportFeedbackButton's post-submit
          receipt flow and YourIssuesPage's portal-enabled redirect both open
          this panel from wherever they render, not just from the page that
          happens to own the currently-mounted YourSubmissionsBell instance. */}
      <PortalPanelProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dropbox/callback" element={<DropboxCallbackPage />} />

          {/* Protected routes - each page uses DashboardLayout internally */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Projects routes */}
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <ProjectNewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/items/new"
            element={
              <ProtectedRoute>
                <BillableItemCreatePage />
              </ProtectedRoute>
            }
          />

          {/* Organizations routes (Phase 5 Batch 2: renamed from clients) */}
          <Route
            path="/organizations"
            element={
              <ProtectedRoute>
                <OrganizationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/new"
            element={
              <ProtectedRoute>
                <OrganizationCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:id/edit"
            element={
              <ProtectedRoute>
                <OrganizationEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:id"
            element={
              <ProtectedRoute>
                <OrganizationEditPage />
              </ProtectedRoute>
            }
          />

          {/* Billable Items routes */}
          <Route
            path="/billable-items/:id"
            element={
              <ProtectedRoute>
                <BillableItemDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Invoice routes */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoiceListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/billing-session"
            element={
              <ProtectedRoute>
                <BillingSessionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute>
                <InvoiceCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Deliverables dashboard - Phase 10b.7 */}
          <Route
            path="/deliverables"
            element={
              <ProtectedRoute>
                <DeliverablesDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Files route - File Management Feature */}
          <Route
            path="/files"
            element={
              <ProtectedRoute>
                <FilesPage />
              </ProtectedRoute>
            }
          />

          {/* Permits routes - Phase 9.16b */}
          <Route
            path="/permits"
            element={
              <ProtectedRoute>
                <PermitsPage />
              </ProtectedRoute>
            }
          />

          {/* Billable Items List - Unified page with tabs */}
          <Route
            path="/billable"
            element={
              <ProtectedRoute>
                <BillableItemsPage />
              </ProtectedRoute>
            }
          />
          {/* Redirect old routes to new unified page */}
          <Route
            path="/billable/ready"
            element={<Navigate to="/billable?view=ready" replace />}
          />
          <Route
            path="/billable/all"
            element={<Navigate to="/billable" replace />}
          />

          {/* Reports Page - Phase 6 Batch 3 */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Settings Page - Phase 6 */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Notifications Page - Phase 9.3c */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Roadmap — dedicated build-track journey page (SPEC-keystone-roadmap-page-2026-07-26
              R2). Authed, same ProtectedRoute gate as every other in-app page.
              Curated static content from R1; component is presentation-only
              (RoadmapPageLayout) so a future public route can reuse it. */}
          <Route
            path="/roadmap"
            element={
              <ProtectedRoute>
                <RoadmapPage />
              </ProtectedRoute>
            }
          />

          {/* Your Issues — the Report Feedback receipt destination (SPEC-stakeholder-portal R3).
              Minimal own-reported-issues list for a NON-portal-enabled client. A
              portal-enabled client is redirected + opens the header panel instead
              (SPEC-stakeholder-portal-presentation-redesign R3 AC6 — the retired
              /stakeholder-portal route no longer exists to navigate to). */}
          <Route
            path="/your-issues"
            element={
              <ProtectedRoute>
                <YourIssuesPage />
              </ProtectedRoute>
            }
          />

          {/* /stakeholder-portal and /coordination are RETIRED as standalone
              left-nav pages (SPEC-stakeholder-portal-presentation-redesign R3
              AC6) — their content now lives in the header panel
              (YourSubmissionsBell -> PanelBody's My Submissions / Roadmap /
              Coordination / All Issues tabs, role-scaled). Intentionally no
              route registered for either path any more. */}

          {/* Portal action item — public, no auth, identity captured inline (R3) */}
          <Route path="/portal/:token/action/:actionId" element={<PortalActionItemPage />} />

          {/* External signer SPA — Phase 4 R1. Public, token-gated via path. */}
          <Route path="/sign/:signRef" element={<SigningPage />} />

          {/* Portal public view - /portal/:token (share links from GET /api/projects/{id}/share) */}
          <Route path="/portal/:token" element={<ExternalViewPage />} />

          {/* External Views - redirects unauthenticated users to /ext/:token/login (Phase 10d.4) */}
          <Route path="/ext/:token" element={<ExternalViewPage />} />

          {/* External View Login - branded login page keyed by view token (Phase 10d.4 Rev 2) */}
          <Route path="/ext/:token/login" element={<ExternalLoginPage />} />

          {/* External View Setup - invitation acceptance flow keyed by acceptance_token (Phase 10d.4 Rev 2) */}
          <Route path="/ext/:token/setup" element={<PortalAcceptInvitationPage />} />

          {/* Portal: Accept invitation link from invitation email (legacy query-param route) */}
          <Route path="/portal/accept-invitation" element={<PortalAcceptInvitationPage />} />

          {/* Portal: Tenant-branded login page (Phase 10d.4) */}
          {/* Supports /portal/:tenant/login and /portal/login?tenant=xyz */}
          <Route path="/portal/:tenant/login" element={<PortalLoginPage />} />
          <Route path="/portal/login" element={<PortalLoginPage />} />

          {/* My Views - planTEK: landing page for EXTERNAL role users */}
          <Route
            path="/my-views"
            element={
              <ProtectedRoute allowExternal={true}>
                <MyViewsPage />
              </ProtectedRoute>
            }
          />

          {/* Site Visits - Phase 10d */}
          <Route
            path="/projects/:projectId/site-visits"
            element={
              <ProtectedRoute>
                <SiteVisitsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/site-visits/:visitId"
            element={
              <ProtectedRoute>
                <SiteVisitDetailPage />
              </ProtectedRoute>
            }
          />
          {/* /site-visits top-level route + nav link removed 2026-05-02
              while the dead-tab redesign is on the roadmap.
              See roadmap: site-visits-tab-redesign. Component file kept
              for the eventual relaunch. */}

          {/* Document Reviews — Phase 13 R2 */}
          <Route
            path="/document-reviews"
            element={
              <ProtectedRoute>
                <DocumentReviewListPage />
              </ProtectedRoute>
            }
          />

          {/* RFIs / Submittals / Supplemental Instructions top-level routes
              redirect to /projects (2026-05-09). Top-level pages were
              placeholders; real workflows live under /projects/:projectId/*.
              Redirects (not 404s) keep old browser bookmarks working.
              See roadmap: rfi-submittals-e2e for eventual cross-project view. */}
          <Route path="/rfis" element={<Navigate to="/projects" replace />} />
          <Route path="/submittals" element={<Navigate to="/projects" replace />} />
          <Route path="/supplemental-instructions" element={<Navigate to="/projects" replace />} />

          {/* User Views - Phase 10a.9 */}
          <Route
            path="/views/:viewId"
            element={
              <ProtectedRoute>
                <UserViewPage />
              </ProtectedRoute>
            }
          />

          {/* planTEK: Portal Management — requires portal_management feature flag */}
          <Route
            path="/portal-management"
            element={
              <ProtectedRoute>
                <FeatureGate feature="portal_management" fallback="message">
                  <PortalManagementPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />

          {/* planTEK: Theme Settings — requires theming feature flag */}
          <Route
            path="/theme-settings"
            element={
              <ProtectedRoute>
                <FeatureGate feature="theming" fallback="message">
                  <ThemeSettingsPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />

          {/* planTEK: ClickUp Integration — requires clickup_integration feature flag */}
          <Route
            path="/clickup"
            element={
              <ProtectedRoute>
                <FeatureGate feature="clickup_integration" fallback="message">
                  <ClickUpConfigPage />
                </FeatureGate>
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      </PortalPanelProvider>
      </ThemeProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;